import dayjs from "dayjs";
import {
    MODEL_REGISTRY,
    type ChatModel,
    isModelAllowedVision,
    getModelConfig,
    getModelProvider, DEFAULT_MODEL } from "../constant";
import { type ChatSession, type ChatMessage, type ChatMessageTextContent, type ChatMessageImageURLContent } from "../interface";
import { generateLocalIdByRole } from "./id";
import { type GPTChatMessageWithToken, type GPTChatMessage, type GPTChatMessageContentImage, type GPTChatMessageContentText } from "./openai";
import { type Message, type Prisma } from "@prisma/client"
import { tokenizer } from "./thirdparty";

// Model type is now just a string (no more enum)
type MessageModel = string

/**
 * Convert model API ID to database format
 * Since we now use strings, this just validates and returns the model ID
 */
export const defaultModelStringToPrisma = (model: string): MessageModel => {
    // Validate the model exists in registry (optional, for safety)
    const config = MODEL_REGISTRY[model as keyof typeof MODEL_REGISTRY];
    if (config) {
        return model; // Use the API ID directly
    }

    // Check if it's a valid model ID from registry values
    const found = Object.values(MODEL_REGISTRY).find(m => m.id === model);
    if (found) {
        return found.id;
    }

    // Return as-is for unknown models (allows new models without code changes)
    return model || DEFAULT_MODEL;
};

/**
 * Convert database model string to ChatModel type
 * Since we now use strings, this is mostly a passthrough with validation
 */
export const defaultModelPrismaToString = (dbModel: MessageModel | null | undefined): ChatModel => {
    if (!dbModel) return 'gpt-4o' as ChatModel;

    // Check if it exists in registry
    if (dbModel in MODEL_REGISTRY) {
        return dbModel as ChatModel;
    }

    // Find by id in registry values
    const found = Object.entries(MODEL_REGISTRY).find(([_, config]) => config.id === dbModel);
    if (found) {
        return found[0] as ChatModel;
    }

    // Return as-is for flexibility (allows new models)
    return dbModel as ChatModel;
};

/**
 * Get model limits (context window and max output tokens)
 * Uses the MODEL_REGISTRY for accurate, up-to-date limits
 */
export const defaultModelGptLimit = (model: ChatModel): { maxWords: number; maxTokens: number } => {
    const config = getModelConfig(model);

    if (config) {
        // Estimate words as ~75% of tokens
        const maxTokens = config.contextWindow;
        const maxWords = Math.floor(maxTokens * 0.75);

        return {
            maxWords,
            maxTokens,
        };
    }

    // Fallback for unknown models
    return {
        maxWords: 15000,
        maxTokens: 16300,
    };
};

// For bubble preview
export const generateChatContentString = (content: ChatMessage['content']) => {
    if (Array.isArray(content)) {
        return content.map(a => a.content).join('\n');
    }
    if (Array.isArray(content.content)) {
        return content.content.join('\n');
    }
    return content?.content || '';
}

// For default instruction shown in chat
export const generateChatStringToContent = (content: string | ChatMessage['content']): ChatMessage['content'] => {
    if (typeof content === 'string') {
        return {
            contentType: 'text',
            content: content
        }
    }
    return content;
}


//~ PARSER
function countWords(text: string | string[]): number {
    if (!text) return 0;
    if (Array.isArray(text)) {
        return text.join(' ').trim().split(/\s+/).length;
    }
    return text.trim().split(/\s+/).length;
}

function countWordsGPTContent(content: GPTChatMessage['content']): number {
    const contentString = getGPTContentToTextString(content)
    return countWords(contentString || '');
}

function countWordsTextContent(content: ChatMessageTextContent): number {
    const contentString = Array.isArray(content?.content)
        ? content.content.join(' ')
        : content.content;

    return countWords(contentString || '');
}

export const getGPTContentToTextString = (gptContent: GPTChatMessage['content']): string => {
    if (typeof gptContent === 'string') {
        return gptContent;
    }
    return gptContent.filter(a => a.type === 'text').map(a => (a as any).text).join(' ');
}

export const getTextContentToTextString = (textContent: ChatMessageTextContent): string => {
    if (!textContent?.content) return ''
    if (typeof textContent?.content === 'string') {
        return textContent?.content;
    }
    if (Array.isArray(textContent?.content)) {
        return textContent?.content.join(' ');
    }
    return ''
}

export const convertGPTContentToChatContent = (gptContent: GPTChatMessage['content']): { content: ChatMessage['content'], contentCustoms?: NonNullable<ChatMessage['contentCustoms']> } => {
    if (typeof gptContent === 'string') return { content: { contentType: 'text', content: gptContent }, contentCustoms: undefined }
    else if (Array.isArray(gptContent)) {
        const contentCustoms = gptContent.filter((a): a is GPTChatMessageContentImage => a.type === 'image_url')
        const content = gptContent.filter((content): content is GPTChatMessageContentText => content.type === 'text').map(a => (a).text).join(' ')
        return {
            content: { contentType: 'text', content },
            contentCustoms: contentCustoms.map(custom => ({
                contentType: 'image_url',
                content: {
                    image_url: {
                        url: custom.image_url.url
                    }
                }
            }))
        }
    }
    return {
        content: { contentType: 'text', content: '' },
        contentCustoms: undefined
    }
}


export function generateGptMessageLimit(messages: ChatMessage[], newMessage: ChatMessage, maxWords: number): GPTChatMessage[] {
    const allMessages = [...messages, newMessage];
    if (allMessages.length < 1) {
        return [];
    }
    let wordCount = 0;
    const gptMessages: GPTChatMessage[] = [];

    const firstMessage = allMessages[0] as ChatMessage;
    wordCount += countWords(firstMessage.content.content || '');

    const lastMessage = allMessages.length > 1 ? allMessages[allMessages.length - 1] : null;
    if (lastMessage) {
        wordCount += countWords(lastMessage.content.content || '');
    }

    if (wordCount > maxWords) {
        throw new Error('First and new messages combined exceed the maximum word limit.');
    }

    // Iterate in reverse order, starting from second the last message
    for (let i = allMessages.length - 2; i > 0; i--) {
        const message = allMessages[i] as ChatMessage;
        const messageWordCount = countWords(message.content.content as string);

        if (wordCount + messageWordCount <= maxWords) {
            gptMessages.unshift({
                role: message.senderRole,
                content: message.content.content as string,
            });
            wordCount += messageWordCount;
        } else {
            const remainingWords = maxWords - wordCount;
            const truncatedContent = truncateContent(message.content, remainingWords);
            gptMessages.unshift({
                role: message.senderRole,
                content: truncatedContent,
            });
            break;
        }
    }
    gptMessages.unshift({
        role: firstMessage.senderRole,
        content: firstMessage.content.content as string,
    });
    if (lastMessage) {
        gptMessages.push({ role: lastMessage.senderRole, content: lastMessage.content.content as string });
    }

    return gptMessages;
}

export const generateGptMessageLimitFromGpt = (gptMessages: GPTChatMessage[], newMessage?: GPTChatMessage, maxWordsPassed?: number): GPTChatMessage[] => {
    const maxWords = maxWordsPassed || 4000;

    const allMessages = newMessage ? [...gptMessages, newMessage] : [...gptMessages];
    let wordCount = 0;
    const outputGptMessages: GPTChatMessage[] = [];

    if (allMessages.length < 1) {
        return [];
    }

    // Add the first message
    const firstMessage = allMessages[0] as GPTChatMessage;
    wordCount += countWordsGPTContent(firstMessage.content);

    // Add the last message or new message
    const lastMessage = allMessages.length > 1 ? allMessages[allMessages.length - 1] : null;
    if (lastMessage) {
        wordCount += countWordsGPTContent(lastMessage.content);
    }

    if (wordCount > maxWords) {
        throw new Error('First and new messages combined exceed the maximum word limit.');
    }


    // Iterate in reverse order, starting from second the last message
    for (let i = allMessages.length - 2; i > 0; i--) {
        const message = allMessages[i] as GPTChatMessage;
        const messageWordCount = countWordsGPTContent(message.content);

        if (wordCount + messageWordCount <= maxWords) {
            outputGptMessages.unshift(message);
            wordCount += messageWordCount;
        } else {
            const remainingWords = maxWords - wordCount;
            const truncatedContent = truncateContentTokens(message.content, remainingWords);
            outputGptMessages.unshift({
                role: message.role,
                content: truncatedContent,
            });
            break;
        }
    }
    outputGptMessages.unshift(firstMessage);
    if (lastMessage) {
        outputGptMessages.push(lastMessage);
    }

    return outputGptMessages;
}

function truncateContent(content: ChatMessage['content'], remainingWords: number): string {
    const contentFixed = getTextContentToTextString(content)
    const words = contentFixed.split(' ');
    let truncated = words.slice(0, remainingWords).join(' ');
    truncated = '..' + truncated.slice(2);

    return truncated;
}

const cacheTokens = new Map<string, number>();

export function messageContentTokens(messageContent: ChatMessageTextContent): number {
    const contentString = Array.isArray(messageContent?.content)
        ? messageContent.content.join(' ')
        : messageContent.content;

    return countTokens(contentString || '');
}

export function messageToMessageToken(message: ChatMessage): ChatMessage {
    if (message?.totalTokens) return message;
    const contentString = Array.isArray(message?.content?.content)
        ? message.content.content.join(' ')
        : message.content.content;

    return {
        ...message,
        totalTokens: countTokens(contentString || ''),
    }
}

export function countTokens(gptContent: GPTChatMessage['content'], tokens?: number): number {
    if (tokens) {
        return tokens;
    }
    const gptContentFixed = getGPTContentToTextString(gptContent)
    if (!cacheTokens.has(gptContentFixed)) {
        cacheTokens.set(gptContentFixed, tokenizer.encode(gptContentFixed).length);
    }
    return cacheTokens.get(gptContentFixed)!;
}

function truncateContentTokens(gptContent: GPTChatMessage['content'], remainingTokens: number): string {
    let tokensCount = 0;
    let tokenIndex = 0;
    let newContent = '';

    const gptContentString = getGPTContentToTextString(gptContent)

    for (const char of gptContentString) {
        if (tokenizer.isWithinTokenLimit(char, 1)) {
            const currentTokens = countTokens(char);
            if (tokensCount + currentTokens <= remainingTokens) {
                tokensCount += currentTokens;
                newContent += char;
                tokenIndex++;
            } else {
                break;
            }
        }
    }
    newContent = '....' + newContent.slice(2);

    cacheTokens.set(newContent, tokensCount);
    return newContent;
}

export const generateGptMessageLimitFromGptTokens = (gptMessages: GPTChatMessageWithToken[], newMessage?: GPTChatMessageWithToken, maxTokensPassed?: number): GPTChatMessage[] => {
    const maxTokens = maxTokensPassed || 4000;

    const allMessages = newMessage ? [...gptMessages, newMessage] : [...gptMessages];
    let tokenCount = 0;
    const outputGptMessages: GPTChatMessage[] = [];

    if (allMessages.length < 1) {
        return [];
    }

    const firstMessage = allMessages[0] as GPTChatMessageWithToken;
    tokenCount += countTokens(firstMessage.content, firstMessage.tokens);

    const lastMessage = allMessages.length > 1 ? allMessages[allMessages.length - 1] : null;
    if (lastMessage) {
        tokenCount += countTokens(lastMessage.content, lastMessage.tokens);
    }

    if (tokenCount > maxTokens) {
        throw new Error('First and new messages combined exceed the maximum token limit.');
    }

    for (let i = allMessages.length - 2; i > 0; i--) {
        const message = allMessages[i] as GPTChatMessageWithToken;
        const messageTokenCount = countTokens(message.content, message.tokens);

        if (tokenCount + messageTokenCount <= maxTokens) {
            outputGptMessages.unshift({
                role: message.role,
                content: message.content,
            });
            tokenCount += messageTokenCount;
        } else {
            const remainingTokens = maxTokens - tokenCount;
            const truncatedContent = truncateContentTokens(message.content, remainingTokens);
            outputGptMessages.unshift({
                role: message.role,
                content: truncatedContent,
            });
            break;
        }
    }
    outputGptMessages.unshift({
        role: firstMessage.role,
        content: firstMessage.content,
    });
    if (lastMessage) {
        outputGptMessages.push({
            role: lastMessage.role,
            content: lastMessage.content,
        });
    }

    return outputGptMessages;
}

//~ GENERATOR
type CompileGptMessagesReturnType<T extends ChatMessage | undefined> = {
    newMessageFixed: T extends ChatMessage ? ChatMessage : undefined;
    gptMessages: GPTChatMessage[];
    initMessage: null;
    initInstruction: string | null;
}

export const compileGptMessages = <T extends ChatMessage | undefined>(session: ChatSession, newMessage: T, maxWords?: number): CompileGptMessagesReturnType<T> => {
    try {
        const initMessage: ChatMessage | null = null
        let initInstruction: string | null = null

        const sessionMessages = session.messages ? [...session.messages] : []
        const refactorMessages: GPTChatMessage[] = sessionMessages.map(a => ({
            content: generateChatContentString(a.content),
            role: a.senderRole,
        })) || []

        //~ PUT INSTRUCTION IN FRONT OF CHAT
        if (session.defaultInstruction) {
            refactorMessages.unshift({
                content: session.defaultInstruction,
                role: "system",
            })
        }


        //~ IF MESSAGE IS EMPTY AND NO INSTRUCTION IN CHAT THEN USE DEFAULT INSTRUCTION
        if (refactorMessages.length === 0 && !session.defaultInstruction) {
            const defaultInstructionMessage = {
                content: "You are a helpful assistant",
                role: "system",
            } as const
            const defaultInstructionChatMessage: ChatMessage = {
                id: generateLocalIdByRole('system'),
                chatSessionId: session.id,
                content: generateChatStringToContent(defaultInstructionMessage.content),
                userId: "",
                senderRole: "developer",
                model: "o1-mini",
                created: dayjs().toDate(),
            }
            sessionMessages.push(defaultInstructionChatMessage)
            refactorMessages.push(defaultInstructionMessage)
            // initMessage = defaultInstructionChatMessage
            initInstruction = defaultInstructionMessage.content
        }

        //~ ADD NEW MESSAGE IF EXISTS
        let newMessageFixed: ChatMessage | undefined = undefined
        if (newMessage) {
            const previousMessage = session.messages.length > 0 ? session.messages[session.messages.length - 1] : null;
            newMessageFixed = {
                ...newMessage,
                ...(previousMessage && previousMessage?.senderRole !== 'system')
                    ? { parentId: previousMessage.id }
                    : {}
            }

            //~ COMBINE WITH USER MESSAGE
            //~ ADD IMAGE IF ALLOWED AND EXIST
            //~ ELSE JUST ADD (DEFAULT)
            if (newMessage?.contentCustoms && session?.defaultModel && isModelAllowedVision(session?.defaultModel)) {
                const imageContents = newMessage?.contentCustoms
                refactorMessages.push({
                    content: [
                        {
                            type: 'text',
                            text: generateChatContentString(newMessage.content),
                        },
                        ...imageContents.map(img => ({
                            type: img.contentType,
                            ...img.content
                        }))
                    ],
                    role: newMessage.senderRole,
                })
            } else {
                refactorMessages.push({
                    content: generateChatContentString(newMessage.content),
                    role: newMessage.senderRole,
                })
            }
        }

        return {
            // Fixed new message format
            newMessageFixed: newMessageFixed as T extends ChatMessage ? ChatMessage : undefined,
            // gpt api messages
            gptMessages: generateGptMessageLimitFromGptTokens(refactorMessages, undefined, maxWords || 3900),
            // previous message if needed
            initMessage,
            // instruction
            initInstruction,
        }
    } catch (error) {
        console.error(error)
        return {
            newMessageFixed: newMessage as unknown as T extends ChatMessage ? ChatMessage : undefined,
            gptMessages: [],
            initMessage: null,
            initInstruction: null,
        }
    }
}

export const messageToChatMessage = (message: Message): ChatMessage => {
    return {
        ...message,
        id: message.localId || message.id,
        parentId: message.parentId || undefined,
        replyToId: message.replyToId || undefined,
        userId: message.userId || undefined,
        model: message.model ? defaultModelPrismaToString(message.model) : undefined,
        updated: message.updated || undefined,
        totalTokens: message.totalTokens || undefined,
        contentCustoms: message.contentCustoms as ChatMessageImageURLContent[] | undefined,
    }
}

export const chatMessageToMessage = (cm: ChatMessage): Message => {
    // Extract model to avoid spread type conflict
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { model: _model, ...rest } = cm;
    // Model is now stored as string directly (no more enum conversion needed)
    const convertedModel = cm.model ? defaultModelStringToPrisma(cm.model) : null;

    return {
        ...rest,
        localId: cm.localId || cm.id,
        parentId: cm.parentId || null,
        replyToId: cm.replyToId || null,
        userId: cm.userId || null,
        content: {
            content: cm.content.content,
            contentType: cm?.content?.contentType || 'text',
        },
        contentCustoms: null,
        created: typeof cm.created === 'string' ? dayjs(cm.created).toDate() : cm.created,
        updated: cm.updated ? (typeof cm.updated === 'string' ? dayjs(cm.updated).toDate() : cm.updated) : null,
        status: null,
        // Type assertion needed until Prisma types are regenerated after migration
        model: convertedModel as Message['model'],
        provider: cm.provider ?? null,
        totalTokens: cm.totalTokens || null,
    }
}

export const chatMessageManyInputToMessage = (cm: Prisma.MessageCreateManyInput): Message => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return {
        ...cm,
        contentCustoms: (cm.contentCustoms as ChatMessageImageURLContent[]) || null,
        totalTokens: cm.totalTokens || null,
        userId: null,
        parentId: cm?.parentId || null,
        replyToId: cm?.replyToId || null,
        model: cm?.model || null,
        provider: cm?.provider ?? null,
        status: cm?.status || null,
        created: cm.created as Date,
        updated: cm.updated as Date,
    }

}

// Note: Legacy image generation (chatImageGenPrompts, chatImageGenParseResult, chatFormatImageGen) 
// removed - use AI SDK tools for image generation instead
